/**
 * use-supabase-queries.ts — Read hooks (topics, atoms, dialogue, bookmarks)
 * Split from use-supabase.ts per #566.
 */
import { getSupabase } from "@/lib/supabase";
import { createLogger } from '@/lib/logger';
import { useSupabaseQuery, type UseQueryResult } from './use-supabase-core';
import { sanitizeForIlike } from './use-supabase-search';
import type {
  AtomRow,
  TopicRow,
  TopicSummaryRow,
  TopicAtomLink,
  DiscussionMessageRow,
  TopicListItem,
} from "@shared-types/api-rows";

const log = createLogger('UseSupabase');

/**
 * Normalize Supabase topic_summaries row — DB uses *_json column names,
 * but our TopicSummaryRow type expects key_points/blind_spots/index_links.
 */
function normalizeSummary(raw: Record<string, unknown>): TopicSummaryRow {
  return {
    ...raw,
    key_points: raw.key_points ?? raw.key_points_json ?? [],
    blind_spots: raw.blind_spots ?? raw.blind_spots_json ?? [],
    index_links: raw.index_links ?? raw.index_links_json ?? [],
  } as TopicSummaryRow;
}

// ── Topics ───────────────────────────────────────────


export function useTopics(params?: {
  status?: string;
  limit?: number;
  offset?: number;
  /** #555: Skip fetch when topics aren't needed */
  skip?: boolean;
}): UseQueryResult<{ topics: TopicListItem[]; total: number }> {
  const limit = params?.limit ?? 20;
  const offset = params?.offset ?? 0;
  const status = params?.status ?? "active";  // Default: only show active topics
  const skip = params?.skip ?? false;

  return useSupabaseQuery(
    skip
      ? null
       : async () => {
      const sb = getSupabase();
      let q = sb
        .from("topics")
        .select("*, topic_atom_links(count)", { count: "exact" })
        .order("created_at", { ascending: false })
        .range(offset, offset + limit - 1);
      q = q.eq("status", status);
      const { data, error, count } = await q;
      if (error) throw new Error(error.message);
      // Compute atom_count from the joined count
      const topics = (data ?? []).map((t: Record<string, unknown>) => {
        const atom_count = Array.isArray(t.topic_atom_links) ? (t.topic_atom_links as { count: number }[])[0]?.count ?? 0 : 0;
        const { topic_atom_links: _, ...rest } = t;
        return { ...rest, atom_count } as TopicListItem;
      });
      // #651: Removed DEV fallback — let empty results show real empty state
      return { topics, total: count ?? 0 };
    },
    [status, limit, offset, skip],
    skip ? undefined : `topics:${status ?? 'all'}:${limit}:${offset}`,
  );
}

export function useTopic(id: string | undefined | null): UseQueryResult<{
  topic: TopicRow;
  summary: TopicSummaryRow | null;
}> {
  return useSupabaseQuery(
    id
      ? async () => {
          const sb = getSupabase();
          const [topicRes, summaryRes] = await Promise.all([
            sb.from("topics").select("*").eq("id", id).single(),
            sb.from("topic_summaries").select("*").eq("topic_id", id).maybeSingle(),
          ]);
          if (topicRes.error) {
            // #651: Removed DEV fallback — surface errors clearly
            throw new Error(topicRes.error.message);
          }
          return {
            topic: topicRes.data as TopicRow,
            summary: summaryRes.data ? normalizeSummary(summaryRes.data) : null,
          };
        }
      : null,
    [id],
    id ? `topic:${id}` : undefined,
  );
}

export function useTopicAtoms(
  topicId: string | undefined | null,
  params?: { limit?: number; offset?: number },
): UseQueryResult<{ atoms: AtomRow[]; links: TopicAtomLink[] }> {
  const limit = params?.limit ?? 20;
  const offset = params?.offset ?? 0;

  return useSupabaseQuery(
    topicId
      ? async () => {
          const sb = getSupabase();
          // #561: Single query with join to avoid N+1 sequential pattern
          // Get links with embedded atom data via foreign key join
          const linksRes = await sb
            .from("topic_atom_links")
            .select("*, idea_atoms(*)")
            .eq("topic_id", topicId)
            .range(offset, offset + limit - 1);
          if (linksRes.error) throw new Error(linksRes.error.message);
          const rawLinks = (linksRes.data ?? []);
          if (!Array.isArray(rawLinks)) {
            log.warn('useTopicAtoms: unexpected data shape', { type: typeof rawLinks });
            return { atoms: [], links: [] };
          }
          const links: TopicAtomLink[] = rawLinks.map((l: Record<string, unknown>) => {
            const { idea_atoms: _, ...link } = l;
            return link as unknown as TopicAtomLink;
          });
          const atoms: AtomRow[] = rawLinks
            .map((l: Record<string, unknown>) => l.idea_atoms as AtomRow | null)
            .filter((a): a is AtomRow => a !== null)
            // #privacy: exclude private atoms — only public/anonymous should appear in topic views
            .filter((a) => a.visibility !== 'private');
          return { atoms, links };
        }
      : null,
    [topicId, limit, offset],
  );
}

/** Sort modes for topic atom ranking */
export type TopicSortMode = 'recommended' | 'newest' | 'most_discussed' | 'most_diverse';

/** Ranked atom with score */
export interface RankedAtomRow extends AtomRow {
  author_name?: string;
  author_avatar?: string;
  rank_score?: number;
}

/**
 * Fetch ranked atoms for a topic using the get_topic_ranked_atoms RPC.
 * Falls back to simple query if RPC fails.
 */
export function useTopicRankedAtoms(
  topicId: string | undefined | null,
  params?: { limit?: number; offset?: number; sort?: TopicSortMode; userId?: string },
): UseQueryResult<RankedAtomRow[]> {
  const limit = params?.limit ?? 20;
  const offset = params?.offset ?? 0;
  const sort = params?.sort ?? 'recommended';
  const userId = params?.userId ?? null;

  return useSupabaseQuery(
    topicId
      ? async () => {
          const sb = getSupabase();
          // Try RPC first
          const { data, error } = await sb.rpc('get_topic_ranked_atoms', {
            p_topic_id: topicId,
            p_user_id: userId,
            p_limit: limit,
            p_offset: offset,
            p_sort: sort,
          });

          if (error) {
            log.warn('get_topic_ranked_atoms RPC failed, falling back', { error: error.message });
            // Fallback: simple join query with client-side sort
            let query = sb
              .from('topic_atom_links')
              .select('*, idea_atoms(*)')
              .eq('topic_id', topicId);
            // Apply sort order at query level when possible
            if (sort === 'newest') {
              query = query.order('created_at', { foreignTable: 'idea_atoms', ascending: false });
            }
            query = query.range(offset, offset + limit - 1);
            const fallback = await query;
            if (fallback.error) throw new Error(fallback.error.message);
            const atoms = (fallback.data ?? [])
              .map((l: Record<string, unknown>) => l.idea_atoms as RankedAtomRow | null)
              .filter((a): a is RankedAtomRow => a !== null && a.visibility !== 'private');
            // Client-side sort for modes that need it
            if (sort === 'newest') {
              atoms.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
            } else if (sort === 'most_discussed') {
              atoms.sort((a, b) => (b.comment_count ?? 0) - (a.comment_count ?? 0) || (b.like_count ?? 0) - (a.like_count ?? 0));
            } else {
              // 'recommended': sort by engagement (like+comment) as best-effort fallback
              atoms.sort((a, b) => (b.like_count + b.comment_count) - (a.like_count + a.comment_count));
            }
            return atoms;
          }

          // RPC returns json array or null
          const atoms: RankedAtomRow[] = Array.isArray(data) ? data : [];
          return atoms;
        }
      : null,
    [topicId, limit, offset, sort, userId],
  );
}

export function useTopicDiscussions(
  topicId: string | undefined | null,
  params?: { limit?: number; offset?: number },
): UseQueryResult<{ messages: DiscussionMessageRow[] }> {
  const limit = params?.limit ?? 50;
  const offset = params?.offset ?? 0;

  return useSupabaseQuery(
    topicId
      ? async () => {
          const sb = getSupabase();
          // 1. Fetch messages
          const { data, error } = await sb
            .from("discussion_messages")
            .select("*")
            .eq("topic_id", topicId)
            .order("created_at", { ascending: true })
            .range(offset, offset + limit - 1);
          if (error) throw new Error(error.message);
          const msgs = (data ?? []) as DiscussionMessageRow[];
          if (msgs.length === 0) return { messages: msgs };

          // 2. Batch-fetch profiles for all unique authors
          const authorIds = [...new Set(msgs.map(m => m.author_user_id))];
          const { data: profiles } = await sb
            .from("profiles")
            .select("user_id, display_name, avatar_url")
            .in("user_id", authorIds);
          const profileMap = new Map((profiles ?? []).map((p: { user_id: string; display_name?: string; avatar_url?: string | null }) => [p.user_id, p]));

          // 3. Merge profile info into messages
          const messages: DiscussionMessageRow[] = msgs.map(m => {
            const p = profileMap.get(m.author_user_id);
            return {
              ...m,
              author_display_name: p?.display_name ?? undefined,
              author_avatar_url: p?.avatar_url ?? null,
            };
          });
          return { messages };
        }
      : null,
    [topicId, limit, offset],
  );
}

// ── Atoms ────────────────────────────────────────────

export function useAtom(
  id: string | undefined | null,
): UseQueryResult<{ atom: AtomRow }> {
  return useSupabaseQuery(
    id
      ? async () => {
          const sb = getSupabase();
          const { data, error } = await sb
            .from("idea_atoms")
            .select("*")
            .eq("id", id)
            .single();
          if (error) {
            // #651: Removed DEV fallback — surface errors clearly
            throw new Error(error.message);
          }
          return { atom: data as AtomRow };
        }
      : null,
    [id],
  );
}

export function useAtoms(params?: {
  visibility?: string;
  limit?: number;
  offset?: number;
  owner?: string;
  /** #272: Skip fetch when atoms aren't needed */
  skip?: boolean;
}): UseQueryResult<{ atoms: AtomRow[]; total: number }> {
  const limit = params?.limit ?? 20;
  const offset = params?.offset ?? 0;
  const visibility = params?.visibility;
  const owner = params?.owner;
  const skip = params?.skip ?? false;

  return useSupabaseQuery(
    skip
      ? null
       : async () => {
          const sb = getSupabase();
          let q = sb
            .from("idea_atoms")
            .select("*", { count: "exact" })
            .order("created_at", { ascending: false })
            .range(offset, offset + limit - 1);
          if (visibility) q = q.eq("visibility", visibility);
          if (owner) q = q.eq("owner_user_id", owner);
          // #398: Exclude anonymous atoms from non-owner queries (privacy)
          if (!owner && !visibility) q = q.neq("visibility", "anonymous");
          const { data, error, count } = await q;
          if (error) throw new Error(error.message);
          return { atoms: (data ?? []) as AtomRow[], total: count ?? 0 };
        },
    [visibility, limit, offset, owner, skip],
    skip ? undefined : `atoms:${owner ?? 'all'}:${visibility ?? 'all'}:${limit}:${offset}`,
  );
}

// ── Dialogue ─────────────────────────────────────────
// #268: These are DB-row shapes (flat columns), distinct from engine's DialogueTurn/DialogueArtifact.
// Canonical engine types: @shared-types/dialogue-artifact. These are Supabase query result shapes.

// ── Atom Feed (v7 redesign) ───────────────────────────

/** Atom + author profile + interaction counts from atom_feed_view */
export interface AtomFeedItem extends AtomRow {
  author_name: string;
  author_avatar: string | null;
  topic_id: string | null;
  like_count: number;
  comment_count: number;
  repost_count: number;
}

/**
 * useAtomFeed — Atom 推荐 Feed 专用 hook (topics-page-v7)
 *
 * Single query against `atom_feed_view` (DB view) which joins:
 * - profiles (author name/avatar)
 * - topic_atom_links (topic_id)
 * - redundant like_count/comment_count/repost_count
 */
export function useAtomFeed(params?: {
  limit?: number;
  offset?: number;
}): UseQueryResult<{ atoms: AtomFeedItem[]; total: number }> {
  const limit = params?.limit ?? 10;
  const offset = params?.offset ?? 0;

  return useSupabaseQuery(
    async () => {
      const sb = getSupabase();

      // Single query against the DB view — no manual joins needed
      const { data, error, count } = await sb
        .from("atom_feed_view")
        .select("*", { count: "exact" })
        .eq("visibility", "public")
        .order("created_at", { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) throw new Error(error.message);

      return {
        atoms: (data ?? []) as AtomFeedItem[],
        total: count ?? 0,
      };
    },
    [limit, offset],
    `atom-feed:${limit}:${offset}`,
  );
}

export interface DialogueTurnData {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  sequence: number;
  redacted?: boolean;
}

export interface DialogueHighlightData {
  id: string;
  start_turn: number;
  end_turn: number;
  label: string;
  note?: string;
}

export interface DialogueArtifactData {
  id: string;
  owner_user_id: string;
  source_type: string;
  visibility: string;
  redaction_state: string;
  turn_count: number;
  duration_minutes: number | null;
  turns: DialogueTurnData[];
  highlights: DialogueHighlightData[];
  summary: string | null;
  linked_atom_ids: string[];
  created_at: string;
  updated_at: string;
}

export function useDialogueArtifact(
  atomId: string | undefined | null,
): UseQueryResult<{ dialogue: DialogueArtifactData }> {
  return useSupabaseQuery(
    atomId
      ? async () => {
          const sb = getSupabase();
          // #562: 1+4 pattern (1 sequential lookup for artifact_id, then 4 parallel)
          // The first query is necessary to get the artifact ID from the atom.
          const atomRes = await sb
            .from("idea_atoms")
            .select("dialogue_artifact_id")
            .eq("id", atomId)
            .single();
          if (atomRes.error) throw new Error(atomRes.error.message);
          const artId = atomRes.data.dialogue_artifact_id;
          if (!artId) throw new Error("No dialogue artifact linked to this atom");
          // Get artifact + turns + highlights
          const [artRes, turnsRes, highlightsRes, linkedAtomsRes] = await Promise.all([
            sb.from("dialogue_artifacts").select("*").eq("id", artId).single(),
            sb.from("dialogue_turns").select("*").eq("artifact_id", artId).order("sequence"),
            sb.from("dialogue_highlights").select("*").eq("artifact_id", artId).order("start_turn"),
            // #283: Query linked atoms instead of hardcoding []
            sb.from("idea_atoms").select("id").eq("dialogue_artifact_id", artId),
          ]);
          if (artRes.error) throw new Error(artRes.error.message);
          return {
            dialogue: {
              ...artRes.data,
              turns: (turnsRes.data ?? []) as DialogueTurnData[],
              highlights: (highlightsRes.data ?? []) as DialogueHighlightData[],
              linked_atom_ids: (linkedAtomsRes.data ?? []).map((r: { id: string }) => r.id),
            } as DialogueArtifactData,
          };
        }
      : null,
    [atomId],
  );
}

// ── Cloud Dialogues ──────────────────────────────────

export interface CloudDialogueListItem {
  id: string;
  source_type: string;
  summary: string | null;
  turn_count: number;
  visibility: string;
  created_at: string;
}

/**
 * #707: useCloudDialogues — fetch dialogue_artifacts for a given user
 * Returns list sorted by created_at DESC, limit 100.
 */
export function useCloudDialogues(
  userId: string | undefined | null,
): UseQueryResult<{ dialogues: CloudDialogueListItem[] }> {
  return useSupabaseQuery(
    userId
      ? async () => {
          const sb = getSupabase();
          const { data, error } = await sb
            .from("dialogue_artifacts")
            .select("id, source_type, summary, turn_count, visibility, created_at")
            .eq("owner_user_id", userId)
            .order("created_at", { ascending: false })
            .limit(100);
          if (error) throw new Error(error.message);
          return { dialogues: (data ?? []) as CloudDialogueListItem[] };
        }
      : null,
    [userId],
    userId ? `cloud-dialogues:${userId}` : undefined,
  );
}

/**
 * #707: useDialogueArtifactById — fetch a dialogue artifact directly by artifactId
 * Similar to useDialogueArtifact but skips the atom → artifact lookup step.
 */
export function useDialogueArtifactById(
  artifactId: string | undefined | null,
): UseQueryResult<{ dialogue: DialogueArtifactData }> {
  return useSupabaseQuery(
    artifactId
      ? async () => {
          const sb = getSupabase();
          const [artRes, turnsRes, highlightsRes, linkedAtomsRes] = await Promise.all([
            sb.from("dialogue_artifacts").select("*").eq("id", artifactId).single(),
            sb.from("dialogue_turns").select("*").eq("artifact_id", artifactId).order("sequence"),
            sb.from("dialogue_highlights").select("*").eq("artifact_id", artifactId).order("start_turn"),
            sb.from("idea_atoms").select("id").eq("dialogue_artifact_id", artifactId),
          ]);
          if (artRes.error) throw new Error(artRes.error.message);
          return {
            dialogue: {
              ...artRes.data,
              turns: (turnsRes.data ?? []) as DialogueTurnData[],
              highlights: (highlightsRes.data ?? []) as DialogueHighlightData[],
              linked_atom_ids: (linkedAtomsRes.data ?? []).map((r: { id: string }) => r.id),
            } as DialogueArtifactData,
          };
        }
      : null,
    [artifactId],
    artifactId ? `dialogue-artifact-by-id:${artifactId}` : undefined,
  );
}

/**
 * #382: Query bookmarked atoms from Supabase (bookmarks table + JOIN idea_atoms)
 * Falls back to empty array in mock mode or when bookmarks table is empty.
 */

/**
 * #709: Search cloud dialogues by keyword (ilike on dialogue_turns.content)
 * Returns matching dialogue_artifacts sorted by created_at DESC.
 */
export function useSearchCloudDialogues(
  userId: string | undefined | null,
  query: string | undefined | null,
): UseQueryResult<{ dialogues: CloudDialogueListItem[] }> {
  const trimmed = query?.trim() ?? '';
  return useSupabaseQuery(
    userId && trimmed.length >= 2
      ? async () => {
          const sb = getSupabase();
          // Search dialogue_turns content, then get unique parent artifacts
          const safeQ = sanitizeForIlike(trimmed);
          const { data: matchedTurns, error: tErr } = await sb
            .from("dialogue_turns")
            .select("artifact_id, dialogue_artifacts!inner(owner_user_id)")
            .eq("dialogue_artifacts.owner_user_id", userId)
            .ilike("content", `%${safeQ}%`)
            .limit(200);
          if (tErr) throw new Error(tErr.message);
          const uniqueArtIds = [...new Set((matchedTurns ?? []).map(t => t.artifact_id))];
          if (uniqueArtIds.length === 0) return { dialogues: [] as CloudDialogueListItem[] };
          // Fetch artifact details for matched IDs, filtered by owner
          const { data: artifacts, error: aErr } = await sb
            .from("dialogue_artifacts")
            .select("id, source_type, summary, turn_count, visibility, created_at")
            .eq("owner_user_id", userId)
            .in("id", uniqueArtIds)
            .order("created_at", { ascending: false })
            .limit(30);
          if (aErr) throw new Error(aErr.message);
          return { dialogues: (artifacts ?? []) as CloudDialogueListItem[] };
        }
      : null,
    [userId, trimmed],
    trimmed.length >= 2 ? `search-cloud-dialogues:${userId}:${trimmed}` : undefined,
  );
}

export function useBookmarkedAtoms(userId: string | null): UseQueryResult<AtomRow[]> {
  return useSupabaseQuery(
    userId
      ? async () => {
          const sb = getSupabase();
          // Query bookmarks table for this user, then fetch matching atoms
          const { data: bookmarks, error: bErr } = await sb
            .from("bookmarks")
            .select("target_id")
            .eq("user_id", userId)
            .eq("target_type", "atom");

          if (bErr) throw new Error(bErr.message);
          if (!bookmarks || bookmarks.length === 0) return [];

          const atomIds = bookmarks.map((b) => b.target_id);
          const { data: atoms, error: aErr } = await sb
            .from("idea_atoms")
            .select("*")
            .in("id", atomIds)
            .order("created_at", { ascending: false });

          if (aErr) throw new Error(aErr.message);
          return (atoms ?? []) as AtomRow[];
        }
      : null,
    [userId],
  );
}

// ── Ranked Feed (recommendation system) ──────────────

/** Feed item returned by get_ranked_feed RPC — extends AtomFeedItem with ranking metadata */
export interface RankedFeedItem extends AtomFeedItem {
  hot_score: number;
  rank_score: number;
  rank_reason: string;
}

/**
 * useRankedFeed — calls the get_ranked_feed RPC for multi-factor ranked results.
 *
 * Factors: hot_score, quality, author diversity, impression penalty, random shuffle.
 * Pass `seenIds` to demote already-viewed atoms.
 * Pass `seed` to control shuffle (changes hourly by default).
 */
export function useRankedFeed(params?: {
  userId?: string | null;
  limit?: number;
  offset?: number;
  seenIds?: string[];
  seed?: number;
}): UseQueryResult<{ atoms: RankedFeedItem[]; total: number }> {
  const userId = params?.userId ?? null;
  const limit = params?.limit ?? 10;
  const offset = params?.offset ?? 0;
  const seenIds = params?.seenIds ?? [];
  const seed = params?.seed ?? null;
  // Stable stringified key for seenIds to avoid infinite re-renders
  const seenKey = seenIds.length > 0 ? seenIds.slice(0, 20).join(',') : '';

  return useSupabaseQuery(
    async () => {
      const sb = getSupabase();

      // Try ranked RPC first; fall back to simple view if RPC not yet deployed
      try {
        const { data, error } = await sb.rpc('get_ranked_feed', {
          p_user_id: userId ?? '',
          p_limit: limit,
          p_offset: offset,
          p_seen_csv: seenIds.join(','),
          p_seed: seed ?? 0,
        });
        if (error) throw error;
        return {
          atoms: (data ?? []) as RankedFeedItem[],
          total: (data ?? []).length >= limit ? limit * 10 : (data ?? []).length,
        };
      } catch (rpcErr) {
        // Fallback: simple created_at DESC from atom_feed_view
        log.warn('Ranked feed RPC unavailable, falling back to time-sorted', { err: String(rpcErr) });
        const { data, error, count } = await sb
          .from('atom_feed_view')
          .select('*', { count: 'exact' })
          .eq('visibility', 'public')
          .order('created_at', { ascending: false })
          .range(offset, offset + limit - 1);
        if (error) throw new Error(error.message);
        return {
          atoms: ((data ?? []) as RankedFeedItem[]).map(a => ({
            ...a,
            hot_score: 0,
            rank_score: 0,
            rank_reason: 'recent',
          })),
          total: count ?? 0,
        };
      }
    },
    [userId, limit, offset, seenKey, seed],
    `ranked-feed:${userId ?? 'anon'}:${limit}:${offset}:${seed}`,
  );
}